import React, { useState, useEffect, useMemo } from "react"
import {
  X, Package, Calendar, Hash, Loader, AlertTriangle, ChevronDown, ChevronRight,
  CheckCircle, XCircle, FileText,
} from "lucide-react"
import { useTheme } from "../contexts/ThemeContext"

const API_URL = process.env.REACT_APP_API_URL

/**
 * Modal de asignación MASIVA de albaranes a varios pedidos.
 *
 * Por cada pedido seleccionado:
 *   1. Carga `/albaran-preview` para conocer modo de stock + líneas del pedido.
 *   2. Muestra una card colapsable.
 *   3. Si el cliente está en modo 'albaran' o 'mixto', la card permite
 *      rellenar cantidad/lote/fecha por línea (lote y fecha opcionales).
 *   4. Si el cliente está en otro modo (picking/inactivo), solo se guarda
 *      el número de albarán en `pedidos.albaran`, sin gestionar stock.
 *
 * Al confirmar, dispara en paralelo:
 *   - PUT /api/pedidos/:id/albaran  (siempre)
 *   - POST /api/albaranes-pendientes (solo para albaran/mixto, si hay líneas)
 *
 * Política de errores: tolerante. Procesa todas las que pueda y al final
 * muestra resumen (OK / Falladas con motivo). Las OK quedan guardadas.
 *
 * Props:
 *   - open
 *   - pedidos       array de pedido objects (al menos { id, npedido, razon_social, proveedor_nombre })
 *   - onClose()
 *   - onApplied()   tras procesar todo, para que el parent refresque
 */
export default function AlbaranMasivoModal({ open, pedidos = [], onClose, onApplied }) {
  const { theme: themeCtx } = useTheme()
  const theme = {
    primary:      themeCtx?.primary      || "#3cbf9a",
    primaryDark:  themeCtx?.primaryDark  || "#2da080",
    primaryLight: themeCtx?.primaryLight || "rgba(60, 191, 154, 0.10)",
  }

  // Estado por pedido: { [pedidoId]: { loading, error, modo_stock, albaran, lineas, expanded, lineasInit } }
  const [datos, setDatos] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [resultados, setResultados] = useState(null) // null mientras no se haya enviado; { ok: [], fail: [] } después

  // El PDF de albarán es POR PEDIDO: cada card tiene su propio archivo + fecha
  // de entrega, guardados en datos[pedidoId].albaranFile / .albaranFecha.

  // Carga inicial de cada pedido al abrir
  useEffect(() => {
    if (!open || pedidos.length === 0) return
    let cancelled = false
    async function loadAll() {
      const token = localStorage.getItem("token")
      // Inicializa estado base (todas en loading)
      const initial = {}
      pedidos.forEach((p, idx) => {
        initial[p.id] = {
          loading: true,
          error: "",
          modo_stock: null,
          albaran: p.albaran || "",
          lineas: [],
          albaranFile: null,
          albaranFecha: "",
          // Solo la primera expandida por defecto
          expanded: idx === 0,
        }
      })
      setDatos(initial)
      setResultados(null)

      // Cargar en paralelo el preview de cada pedido
      await Promise.all(pedidos.map(async (p) => {
        try {
          const r = await fetch(`${API_URL}/api/stock/pedido/${p.id}/albaran-preview`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          const d = await r.json()
          if (cancelled) return
          const modo = d.modo_stock || (d.gestion_stock ? "albaran" : "inactivo")
          const necesitaLineas = modo === "albaran" || modo === "mixto"

          // Si necesita líneas, también cargamos las líneas reales del pedido
          // (porque /albaran-preview solo las devuelve cuando modo === 'albaran'
          // en el endpoint viejo; para mixto usaremos /pedidos/:id/lineas).
          let lineas = []
          if (necesitaLineas) {
            if (d.lineas && d.lineas.length > 0) {
              // Vienen del preview (modo albaran)
              lineas = d.lineas.map(l => ({
                lpedido_id: l.linea_id,
                producto_id: l.producto_id,
                nombre: l.nombre,
                unidadbasica: l.unidadbasica || "",
                factor: l.factor_detectado || 1,
                cantidad_pedida: l.cantidad_pedida,
                cantidad_unidades: l.unidades_a_sumar || (l.cantidad_pedida * (l.factor_detectado || 1)),
                lote: "",
                fecha_vencimiento: "",
              }))
            } else {
              // Modo mixto: el preview no devuelve líneas, cargamos manualmente
              const rl = await fetch(`${API_URL}/api/pedidos/${p.id}/lineas`, {
                headers: { Authorization: `Bearer ${token}` },
              })
              const dl = await rl.json()
              if (cancelled) return
              const arr = dl.lineas || dl || []
              lineas = arr.map(l => {
                const factor = parseFactor(l.unidadbasica)
                return {
                  lpedido_id: l.id,
                  producto_id: l.idarticulo || l.producto_id,
                  nombre: l.nombre,
                  unidadbasica: l.unidadbasica || "",
                  factor,
                  cantidad_pedida: l.cantidad,
                  cantidad_unidades: (parseInt(l.cantidad) || 0) * factor,
                  lote: "",
                  fecha_vencimiento: "",
                }
              })
            }
          }

          setDatos(prev => ({
            ...prev,
            [p.id]: {
              ...prev[p.id],
              loading: false,
              modo_stock: modo,
              lineas,
            },
          }))
        } catch (e) {
          if (cancelled) return
          setDatos(prev => ({
            ...prev,
            [p.id]: { ...prev[p.id], loading: false, error: e.message },
          }))
        }
      }))
    }
    loadAll()
    return () => { cancelled = true }
  }, [open, pedidos])

  // Resumen de cuántos están listos vs faltantes.
  // OJO: tiene que ir ANTES del early return `if (!open)` para no violar las
  // Rules of Hooks (los hooks deben llamarse en el mismo orden en cada render).
  const resumen = useMemo(() => {
    let conAlbaran = 0
    const total = pedidos.length
    pedidos.forEach(p => {
      const d = datos[p.id]
      if (d && d.albaran && d.albaran.trim()) conAlbaran++
    })
    return { total, conAlbaran, faltan: total - conAlbaran }
  }, [datos, pedidos])

  if (!open) return null

  const toggleExpanded = (pedidoId) => {
    setDatos(prev => ({
      ...prev,
      [pedidoId]: { ...prev[pedidoId], expanded: !prev[pedidoId]?.expanded },
    }))
  }

  const handleAlbaranChange = (pedidoId, value) => {
    setDatos(prev => ({ ...prev, [pedidoId]: { ...prev[pedidoId], albaran: value } }))
  }

  const handlePedidoFileChange = (pedidoId, e) => {
    const file = e.target.files[0]
    if (!file) {
      setDatos(prev => ({ ...prev, [pedidoId]: { ...prev[pedidoId], albaranFile: null } }))
      return
    }
    if (file.type !== "application/pdf") {
      alert("Solo se permiten archivos PDF")
      e.target.value = ""
      return
    }
    if (file.size > 50 * 1024 * 1024) {
      alert("El archivo no puede superar los 50MB")
      e.target.value = ""
      return
    }
    setDatos(prev => ({ ...prev, [pedidoId]: { ...prev[pedidoId], albaranFile: file } }))
  }

  const handlePedidoFechaChange = (pedidoId, value) => {
    setDatos(prev => ({ ...prev, [pedidoId]: { ...prev[pedidoId], albaranFecha: value } }))
  }

  const handleLineaChange = (pedidoId, idx, field, value) => {
    setDatos(prev => ({
      ...prev,
      [pedidoId]: {
        ...prev[pedidoId],
        lineas: prev[pedidoId].lineas.map((l, i) => i === idx ? { ...l, [field]: value } : l),
      },
    }))
  }

  const handleConfirmar = async () => {
    setSubmitting(true)
    const token = localStorage.getItem("token")
    const ok = []
    const fail = []

    // Procesamos secuencialmente para no abrumar al backend con 50 requests
    // concurrentes si el user selecciona muchos pedidos.
    for (const p of pedidos) {
      const d = datos[p.id]
      if (!d || !d.albaran || !d.albaran.trim()) {
        fail.push({ pedido: p, error: "Sin número de albarán" })
        continue
      }
      try {
        // 1) PUT albarán a pedidos.albaran
        const r1 = await fetch(`${API_URL}/api/pedidos/${p.id}/albaran`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ albaran: d.albaran.trim() }),
        })
        if (!r1.ok) {
          const e1 = await r1.json().catch(() => ({}))
          throw new Error(e1.message || `Error al actualizar albarán (${r1.status})`)
        }

        // 2) Si es albaran/mixto, crear albarán pendiente con líneas
        if ((d.modo_stock === "albaran" || d.modo_stock === "mixto") && d.lineas.length > 0) {
          const lineasValidas = d.lineas
            .filter(l => parseInt(l.cantidad_unidades) > 0)
            .map(l => ({
              lpedido_id: l.lpedido_id,
              producto_id: l.producto_id,
              cantidad_unidades: parseInt(l.cantidad_unidades),
              lote: l.lote ? l.lote.trim() : null,
              fecha_vencimiento: l.fecha_vencimiento || null,
            }))

          if (lineasValidas.length > 0) {
            const r2 = await fetch(`${API_URL}/api/albaranes-pendientes`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
              body: JSON.stringify({
                pedido_id: p.id,
                numero: d.albaran.trim(),
                lineas: lineasValidas,
              }),
            })
            if (!r2.ok) {
              const e2 = await r2.json().catch(() => ({}))
              if (r2.status === 409) {
                throw new Error("Ya hay un albarán pendiente activo para este pedido. Acéptalo o deniégalo antes de crear otro.")
              }
              throw new Error(e2.message || `Error al crear albarán pendiente (${r2.status})`)
            }
          }
        }

        // 3) Si este pedido tiene su propio PDF de albarán, adjuntarlo.
        if (d.albaranFile) {
          const fd = new FormData()
          fd.append("file", d.albaranFile)
          if (d.albaranFecha) fd.append("fecha_entrega", d.albaranFecha)
          const r3 = await fetch(`${API_URL}/api/pedidos/${p.id}/albaran-pdf`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          })
          if (!r3.ok) {
            const e3 = await r3.json().catch(() => ({}))
            // El número ya se guardó; marcamos fallo parcial claro y seguimos.
            fail.push({ pedido: p, error: `Nº albarán guardado, pero el PDF falló: ${e3.message || r3.status}` })
            continue
          }
        }

        ok.push({ pedido: p, albaran: d.albaran.trim() })
      } catch (e) {
        fail.push({ pedido: p, error: e.message })
      }
    }

    setResultados({ ok, fail })
    setSubmitting(false)
    onApplied?.({ ok, fail })
  }

  // Pantalla de resultados después de confirmar
  if (resultados) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col" style={{ maxHeight: "85vh" }}>
          <div className="px-6 py-4 border-b" style={{ backgroundColor: theme.primaryLight }}>
            <h2 className="text-lg font-bold text-gray-800">Resultado de la asignación masiva</h2>
            <p className="text-xs text-gray-600 mt-1">
              {resultados.ok.length} guardados · {resultados.fail.length} fallaron
            </p>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: 0 }}>
            {resultados.ok.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <h3 className="text-sm font-bold text-emerald-800 flex items-center gap-1.5 mb-2">
                  <CheckCircle size={14} /> Guardados ({resultados.ok.length})
                </h3>
                <ul className="text-xs text-emerald-700 space-y-1">
                  {resultados.ok.map(o => (
                    <li key={o.pedido.id}>· {o.pedido.npedido || `#${o.pedido.id}`} — {o.pedido.razon_social}</li>
                  ))}
                </ul>
              </div>
            )}
            {resultados.fail.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <h3 className="text-sm font-bold text-red-800 flex items-center gap-1.5 mb-2">
                  <XCircle size={14} /> Fallidos ({resultados.fail.length})
                </h3>
                <ul className="text-xs text-red-700 space-y-1">
                  {resultados.fail.map(f => (
                    <li key={f.pedido.id}>
                      · <strong>{f.pedido.npedido || `#${f.pedido.id}`}</strong> ({f.pedido.razon_social}): {f.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="px-6 py-3 border-t bg-gray-50 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-white rounded"
              style={{ backgroundColor: theme.primary }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col" style={{ maxHeight: "85vh" }}>
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: theme.primaryLight }}>
          <div>
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <FileText size={18} style={{ color: theme.primaryDark }} />
              Asignar albarán a {pedidos.length} pedido{pedidos.length === 1 ? "" : "s"}
            </h2>
            <p className="text-xs text-gray-600 mt-1">
              {resumen.conAlbaran} de {resumen.total} con número de albarán completado
              {resumen.faltan > 0 && <span className="text-amber-700 font-semibold"> · faltan {resumen.faltan}</span>}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/50" disabled={submitting}>
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Body: una card por pedido. El scroll vive aquí (minHeight:0 inline
            para que el flex item pueda encogerse y activar overflow). */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ minHeight: 0 }}>
          {pedidos.map((p) => {
            const d = datos[p.id]
            const necesitaLineas = d && (d.modo_stock === "albaran" || d.modo_stock === "mixto")
            const badge = !d ? null
              : d.loading ? <Loader size={12} className="animate-spin text-gray-400" />
              : d.error ? <AlertTriangle size={12} className="text-red-500" />
              : !d.albaran || !d.albaran.trim() ? <AlertTriangle size={12} className="text-amber-500" />
              : <CheckCircle size={12} className="text-emerald-500" />

            return (
              <div key={p.id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Card header — siempre visible */}
                <button
                  onClick={() => toggleExpanded(p.id)}
                  className="w-full px-3 py-2 bg-gray-50 hover:bg-gray-100 flex items-center gap-2 text-left"
                  type="button"
                >
                  {d?.expanded ? <ChevronDown size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-800 truncate">
                      {p.npedido || `#${p.id}`} · {p.razon_social || "—"}
                    </div>
                    <div className="text-[10px] text-gray-500">
                      {p.proveedor_nombre || "—"}
                      {d?.modo_stock && (
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 uppercase font-semibold text-[9px]">
                          {d.modo_stock}
                        </span>
                      )}
                    </div>
                  </div>
                  {badge}
                </button>

                {/* Card body — expandible (el scroll lo gestiona el body del modal) */}
                {d?.expanded && (
                  <div className="p-3 bg-white">
                    {d.loading ? (
                      <div className="text-xs text-gray-500 py-2"><Loader size={12} className="inline animate-spin mr-1" /> Cargando...</div>
                    ) : d.error ? (
                      <div className="text-xs text-red-600 py-2">⚠ {d.error}</div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <label className="text-xs font-semibold text-gray-700 whitespace-nowrap">Nº albarán</label>
                          <input
                            type="text"
                            value={d.albaran}
                            onChange={(e) => handleAlbaranChange(p.id, e.target.value)}
                            placeholder="Obligatorio"
                            className="flex-1 px-3 py-1.5 text-sm border-2 border-gray-200 rounded focus:outline-none"
                            onFocus={(e) => e.target.style.borderColor = theme.primary}
                            onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
                          />
                        </div>

                        {/* PDF de albarán propio de ESTE pedido (opcional) */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2 p-2 bg-gray-50 rounded">
                          <label className="text-xs font-semibold text-gray-700 whitespace-nowrap flex items-center gap-1">
                            <FileText size={12} style={{ color: theme.primaryDark }} />
                            PDF albarán
                          </label>
                          <input
                            type="file"
                            accept="application/pdf"
                            onChange={(e) => handlePedidoFileChange(p.id, e)}
                            disabled={submitting}
                            className="flex-1 text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-gray-200 file:text-gray-700 hover:file:bg-gray-300 disabled:opacity-50"
                          />
                          <div className="flex items-center gap-1">
                            <Calendar size={12} className="text-gray-400" />
                            <input
                              type="date"
                              value={d.albaranFecha || ""}
                              onChange={(e) => handlePedidoFechaChange(p.id, e.target.value)}
                              disabled={submitting}
                              title="Fecha de entrega (opcional)"
                              className="px-2 py-1 text-xs border-2 border-gray-200 rounded focus:outline-none disabled:opacity-50"
                              onFocus={(e) => e.target.style.borderColor = theme.primary}
                              onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
                            />
                          </div>
                          {d.albaranFile && (
                            <span className="text-[11px] font-medium flex items-center gap-1 whitespace-nowrap" style={{ color: theme.primaryDark }}>
                              <CheckCircle size={11} /> {d.albaranFile.name}
                            </span>
                          )}
                        </div>

                        {!necesitaLineas ? (
                          <p className="text-[11px] text-gray-500 italic">
                            Cliente sin gestión de stock activa. Solo se guardará el número de albarán.
                          </p>
                        ) : (
                          <table className="w-full text-[11px] mt-2">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left px-2 py-1">Producto</th>
                                <th className="text-right px-2 py-1">Pedido</th>
                                <th className="text-right px-2 py-1">Cant. uds.</th>
                                <th className="text-left px-2 py-1">Lote</th>
                                <th className="text-left px-2 py-1">Vencimiento</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.lineas.map((l, idx) => (
                                <tr key={l.lpedido_id} className="border-t">
                                  <td className="px-2 py-1 font-medium text-gray-800 truncate max-w-[200px]">{l.nombre}</td>
                                  <td className="px-2 py-1 text-right text-gray-500">
                                    {l.cantidad_pedida}
                                    {l.factor > 1 && <span className="text-[9px] block">× {l.factor}</span>}
                                  </td>
                                  <td className="px-2 py-1 text-right">
                                    <input
                                      type="number" min="0"
                                      value={l.cantidad_unidades}
                                      onChange={(e) => handleLineaChange(p.id, idx, "cantidad_unidades", e.target.value)}
                                      className="w-20 px-1.5 py-1 text-right border border-gray-200 rounded focus:outline-none"
                                    />
                                  </td>
                                  <td className="px-2 py-1">
                                    <div className="flex items-center gap-1">
                                      <Hash size={10} className="text-gray-400" />
                                      <input
                                        type="text"
                                        value={l.lote}
                                        onChange={(e) => handleLineaChange(p.id, idx, "lote", e.target.value.toUpperCase())}
                                        placeholder="Opcional"
                                        className="w-24 px-1.5 py-1 border border-gray-200 rounded uppercase focus:outline-none"
                                      />
                                    </div>
                                  </td>
                                  <td className="px-2 py-1">
                                    <div className="flex items-center gap-1">
                                      <Calendar size={10} className="text-gray-400" />
                                      <input
                                        type="date"
                                        value={l.fecha_vencimiento}
                                        onChange={(e) => handleLineaChange(p.id, idx, "fecha_vencimiento", e.target.value)}
                                        className="w-32 px-1.5 py-1 border border-gray-200 rounded focus:outline-none"
                                      />
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 flex justify-between gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirmar}
            disabled={submitting || resumen.conAlbaran === 0}
            className="px-4 py-2 text-sm font-semibold text-white rounded flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: theme.primary }}
            title={resumen.conAlbaran === 0 ? "Necesitas rellenar al menos un número de albarán" : ""}
          >
            {submitting ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            Confirmar {resumen.conAlbaran} pedido{resumen.conAlbaran === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  )
}

// Mismo helper que en backend/otros modales: del último número del campo
// "unidadbasica" sacamos el factor. "1 caja de 100" → 100.
function parseFactor(unidadbasica) {
  if (!unidadbasica) return 1
  const matches = String(unidadbasica).match(/\d+/g)
  if (!matches || matches.length < 2) return 1
  const n = parseInt(matches[matches.length - 1], 10)
  return (isNaN(n) || n < 1) ? 1 : n
}
